import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, XCircle, Home, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paymentMethod = searchParams.get("method"); // "paynow" or undefined (stripe)
  
  const [status, setStatus] = useState("loading"); // loading, success, pending_verification, failed, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 10;

  useEffect(() => {
    if (sessionId) {
      if (paymentMethod === "paynow") {
        // For PayNow, check transaction status directly
        checkPayNowStatus();
      } else {
        // For Stripe, poll checkout status
        pollPaymentStatus();
      }
    } else {
      setStatus("error");
    }
  }, [sessionId, paymentMethod]);

  // Check PayNow transaction status (doesn't call Stripe)
  const checkPayNowStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/transaction/${sessionId}`);
      const data = response.data;
      
      setPaymentDetails(data);

      // PayNow payments are "pending_verification" until admin approves
      if (data.payment_status === "paid") {
        setStatus("success");
      } else if (data.status === "pending_verification" || data.payment_status === "pending") {
        setStatus("pending_verification");
      } else if (data.status === "failed" || data.status === "expired") {
        setStatus("failed");
      } else {
        // Default to pending_verification for PayNow
        setStatus("pending_verification");
      }
    } catch (error) {
      console.error("Error checking PayNow status:", error);
      // Even if API fails, show pending_verification for PayNow
      setStatus("pending_verification");
    }
  };

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxPollAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      const data = response.data;
      
      setPaymentDetails(data);

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired" || data.payment_status === "unpaid") {
        setStatus("failed");
        return;
      }

      // Continue polling if still processing
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPollAttempts(prev => prev + 1);
      
      if (pollAttempts < maxPollAttempts - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-slate-400">
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-emerald-400 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-400 mb-6">
              Thank you for registering with SCC Football Academy.
            </p>
            
            {paymentDetails && (
              <div className="bg-[#0a0f1a] rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold mb-3">Payment Details</h3>
                <div className="text-sm space-y-2">
                  {/* Payment Reference - Most Important */}
                  {paymentDetails.payment_reference && (
                    <div className="flex justify-between bg-blue-500/10 p-2 rounded border border-blue-500/30">
                      <span className="text-blue-400 font-medium">Payment Reference:</span>
                      <span className="font-bold text-blue-300">
                        {paymentDetails.payment_reference}
                      </span>
                    </div>
                  )}
                  
                  {paymentDetails.parent_name && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Name:</span>
                      <span className="font-medium">{paymentDetails.parent_name}</span>
                    </div>
                  )}
                  
                  {paymentDetails.parent_email && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Email:</span>
                      <span className="font-medium">{paymentDetails.parent_email}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-slate-400">Amount Paid:</span>
                    <span className="font-medium">
                      ${paymentDetails.amount_total?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Status:</span>
                    <span className="font-medium text-emerald-400">Confirmed</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm text-slate-400">
                A confirmation email will be sent to your registered email address.
                <br />
                <strong>Please save your payment reference for your records.</strong>
              </p>
              <Link to="/">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Home className="w-4 h-4 mr-2" />
                  Return to Home
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-red-400 mb-2">
              Payment Failed
            </h1>
            <p className="text-slate-400 mb-6">
              Your payment could not be processed. Please try again.
            </p>
            <div className="space-y-3">
              <Link to="/academy/start-registration">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Try Again
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Return to Home
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending_verification" && (
          <>
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-amber-400 mb-2">
              Registration Submitted!
            </h1>
            <p className="text-slate-300 mb-4">
              Your registration is being processed. Please wait while admin confirms your payment.
            </p>
            
            {paymentDetails && (
              <div className="bg-amber-500/10 rounded-lg p-4 mb-6 text-left border border-amber-500/30">
                <h3 className="font-semibold mb-3 text-amber-300">Payment Details</h3>
                <div className="text-sm space-y-2">
                  {paymentDetails.payment_reference && (
                    <div className="flex justify-between bg-slate-900/50 p-2 rounded border border-amber-500/30">
                      <span className="text-amber-400 font-medium">Payment Reference:</span>
                      <span className="font-bold text-amber-300">
                        {paymentDetails.payment_reference}
                      </span>
                    </div>
                  )}
                  
                  {paymentDetails.amount && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Amount:</span>
                      <span className="font-medium">${paymentDetails.amount?.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-slate-400">Status:</span>
                    <span className="font-medium text-amber-600">Pending Verification</span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-semibold text-blue-300 mb-2">What happens next?</h4>
              <ul className="text-sm text-blue-400 space-y-2">
                <li>✓ You will receive a notification in this portal once payment is confirmed</li>
                <li>✓ You will also receive an email confirmation</li>
                <li>✓ Your child will be registered for the selected sessions</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-slate-400">
                <strong>Please save your payment reference for your records.</strong>
              </p>
              <Link to="/dashboard">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Home className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Status Unknown
            </h1>
            <p className="text-slate-400 mb-6">
              We couldn't verify your payment status. If you completed the payment, 
              please check your email for confirmation or contact support.
            </p>
            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Return to Home
                </Button>
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
